<template>
    <div class="w-full space-y-3">
        <Head title="Fixtures"/>
        <div class="flex justify-between">
            <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl"><span v-if="archived === true">Archived </span>Fixtures</h1>
        </div>
        <div class="w-full flex gap-3 justify-between">
            <div class="flex gap-3 w-full">
                <Link :href="route('frontend.fixture.create')"
                      class="inline-flex items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Add New Fixture
                </Link>
                <Link v-if="archived === false" :href="route('frontend.fixture.archived')"
                    class="inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                    <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    View Archived Fixtures
                </Link>
                <Link v-else :href="route('frontend.fixture.index')"
                      class="inline-flex items-center px-4 py-2 bg-gray-800 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500">
                    <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    View Fixtures
                </Link>
                <a :href="route('frontend.fixture.export', {
                    _query: {
                        search: this.form.search,
                        filter: this.form.filter
                    }})"
                    class="inline-flex items-center px-4 py-2 border border-transparent rounded-md font-semibold text-xs text-white uppercase bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                    <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                         stroke="currentColor" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    Export Fixture List
                </a>
            </div>
            <div class="w-full flex justify-end justify-content-end align-items-end">
                <search-filter :show-filter="false" v-model:value="form.search" class="w-1/2 max-w-md mr-4" @reset="reset"></search-filter>
            </div>
        </div>
        <div class="w-full flex gap-3 justify-between">
            <div class="flex gap-1 w-full items-center">
                <v-select class="mt-1 block w-1/5 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm" multiple
                    :class="{'border-red-500': form.errors['filter.trading_details']}" :options="filters.trading_details" label="display_name" :reduce="trading_detail => trading_detail.id" v-model="form.filter.trading_details" placeholder="Trading Details" />
                <v-select class="mt-1 block w-1/5 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                    :class="{'border-red-500': form.errors['filter.fixture_type']}" :options="fixture_types" label="name" :reduce="fixture_types => fixture_types.id" v-model="form.filter.fixture_type" placeholder="Type" />
                <v-select class="mt-1 block w-1/5 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                    :class="{'border-red-500': form.errors['filter.status']}" :options="statuses" label="status" :reduce="statuses => statuses.id" v-model="form.filter.status" placeholder="Status" />
                <v-select class="mt-1 block w-1/5 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                    :class="{'border-red-500': form.errors['filter.client']}" :options="clients" label="name" :reduce="clients => clients.id" v-model="form.filter.client" placeholder="Client" />
                <v-select class="mt-1 block w-1/5 bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                    :class="{'border-red-500': form.errors['filter.manager']}" :options="managers" label="full_name" :reduce="managers => managers.id" v-model="form.filter.manager" placeholder="Manager" />
            </div>
        </div>
        <div class="w-full flex gap-3 justify-between">
            <div class="flex gap-1 w-full items-center">
                <div class="block w-1/3">
                    <input v-model="form.filter.starts_at" type="date"
                        name="starts_at" id="starts_at"
                        autocomplete="starts_at"
                        class="flex-grow-1 mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                </div>
                <div class="block w-1/3">
                    <input v-model="form.filter.ends_at" type="date"
                        name="ends_at" id="ends_at"
                        autocomplete="ends_at"
                        class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"/>
                </div>
                <div class="block w-1/3">
                    <div class="flex items-center justify-between w-full">
                        <span class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            Sort By
                        </span>
                        <v-select class="mt-1 block w-full bg-white rounded-md shadow-sm focus:outline-none focus:ring-gray-900 focus:border-gray-900 sm:text-sm"
                            :class="{'border-red-500': form.errors['filter.sort_by']}" :options="sort_by" label="label" :reduce="option => option.id" v-model="form.filter.sort_by" placeholder="Sort By" />
                        <div class="flex gap-2 w-full">
                            <button @click="toggleDirection" class="pt-1 pl-3">
                                <span v-if="form.filter.direction === 'asc'" class="flex">
                                    <ArrowNarrowDownIcon class="w-5 h-5 text-light-pitch-purple"></ArrowNarrowDownIcon> <span class="text-sm text-gray-900 italic">ASC</span>
                                </span>
                                <span v-else-if="form.filter.direction === 'desc'" class="flex">
                                    <ArrowNarrowUpIcon class="w-5 h-5 text-light-pitch-purple"></ArrowNarrowUpIcon> <span class="text-sm text-gray-900 italic">DESC</span>
                                </span>
                                <span v-else>
                                    Direction
                                </span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="bg-white py-6 px-4 sm:p-6 shadow mb-6">
            <div class="flex flex-col mt-3 space-y-3">
                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                            <table class="min-w-full divide-y divide-gray-200">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Fixture / Event
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Reference
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Country
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Client
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Travel Manager
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Fixture Date
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            End Date
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col"
                                            class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-if="fixtures.data.length > 0" v-for="(fixture,index) in fixtures.data"
                                        :key="index"
                                        :class="{'bg-gray-100': index % 2, 'bg-red-100 hover:bg-red-50': fixture.overdue === true && fixture.status !== 'Complete'}" class="hover:bg-gray-50 cursor-pointer">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex">
                                            <img :src="'/images/' + fixture.flag + '.svg'" width="20" />
                                            <a :href="route('frontend.fixture.show' , fixture.id)" class="pl-3">
                                                {{ fixture.title }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.show' , fixture.id)">
                                                {{ fixture.reference }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <a :href="route('frontend.fixture.show' , fixture.id)">
                                                {{ fixture.country }}
                                            </a>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {{ fixture.client }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {{ fixture.type.name }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {{ fixture.travel_manager }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span v-if="fixture.starts_at !== null">{{ moment(fixture.starts_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}</span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span v-if="fixture.ends_at !== null">{{ moment(fixture.ends_at, 'YYYY-MM-DD').format('DD MMM YYYY')}}</span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <span v-if="fixture.status === 'Request'"
                                                class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-gray-500 text-white"> {{
                                                    fixture.status
                                                }} </span>
                                            <span v-if="fixture.status === 'Cost'"
                                                class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-yellow-100 text-yellow-800"> {{
                                                    fixture.status
                                                }} </span>
                                            <span v-if="fixture.status === 'Cancelled'"
                                                class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-red-100 text-red-800"> {{
                                                    fixture.status
                                                }} </span>
                                            <span v-if="fixture.status === 'Complete'"
                                                class="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium shadow-md bg-green-100 text-green-800"> {{
                                                    fixture.status
                                                }} </span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 flex">
                                        <!-- Not an inertia link, this is to specifically cause a full page load when visiting a fixture -->
                                            <Link :href="route('frontend.fixture.show' , fixture.id)">
                                                <PencilAltIcon class="w-5 h-5 text-light-pitch-purple"></PencilAltIcon>
                                            </Link>
                                            <Link v-if="archived === true" :href="route('frontend.fixture.unarchive' , fixture.id)">
                                                <RefreshIcon class="w-5 h-5 text-light-pitch-purple"></RefreshIcon>
                                            </Link>
                                        </td>
                                    </tr>
                                    <tr v-else>
                                        <td colspan="8"
                                            class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            There are no fixtures available for your search criteria.
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <Pagination :links="fixtures.links"/>
            </div>
        </div>
    </div>
</template>

<script>
import moment from "moment/moment";
import pickBy from "lodash/pickBy";
import debounce from "lodash/debounce";
import mapValues from "lodash/mapValues";
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import SearchFilter from "@/Components/SearchFilter";
import {Link, Head, useForm, usePage} from "@inertiajs/vue3";
import {PencilAltIcon, RefreshIcon, ArrowNarrowDownIcon, ArrowNarrowUpIcon} from "@heroicons/vue/outline";


export default {
    layout: Authenticated,
    components: {
        Link,
        Head,
        Pagination,
        RefreshIcon,
        SearchFilter,
        PencilAltIcon,
        ArrowNarrowUpIcon,
        ArrowNarrowDownIcon
    },
    props: ['fixtures', 'filters', 'archived'],
    
    created() {
      this.moment = moment;
    },
    data() {
        const user_role = usePage().props.auth.user_role;

        return {
            form: useForm({
                search: null,
                filter: {
                    trading_details: "",
                    status: "",
                    manager: user_role == 1 || user_role == 4 ? "" : usePage().props.auth.user.id,
                    client: "",
                    fixture_type: "",
                    starts_at: this.filters.starts_at,
                    ends_at: this.filters.ends_at,
                    sort_by: "starts_at",
                    direction: "desc"
                }
            })
        }
    },
    methods: {
        reset() {
            this.form.search = null;
        },
        toggleDirection() {
            if (this.form.filter.direction === 'asc') {
                this.form.filter.direction = 'desc';
            } else {
                this.form.filter.direction = 'asc';
            }
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route(this.archived ? 'frontend.fixture.archived' : 'frontend.fixture.index'), pickBy(this.form), { preserveState: true, preserveScroll: true });
            }, 500),
        },
    },
    computed: {
        fixture_types() {
            return this.filters.fixture_types;
        },
        statuses() {
            const formattedStatuses = [];
    
            // Iterate over the keys and values of the statuses object
            for (const key in this.filters.statuses) {
                if (Object.hasOwnProperty.call(this.filters.statuses, key)) {
                    const id = key;
                    const status = this.filters.statuses[key];
                    
                    formattedStatuses.push({ id, status });
                }
            }
    
            return formattedStatuses;
        },
        clients() {
            return this.filters.clients;
        },
        managers() {
            return this.filters.managers.map(manager => ({
                ...manager,
                full_name: `${manager.first_name} ${manager.last_name}`
            }));
        },
        sort_by() {
            return this.filters.sortby;
        }
    }
}
</script>
